import React, { useState, useEffect } from 'react'
import { styled } from '@mui/material'
import ReactSelect from 'react-select'
import { useLocation, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'

//components
import StyledPageContainer from '../../components/StyledPageContainer/StyledPageContainer'
import Overview from './components/Overview'
import HourlyTrend from './components/HourlyTrend'
import Rejection from './components/Rejection'
import Idletime from './components/Idletime'
import SingleMachineCard from './components/SingleMachineCard'
import DateSelector from '../../components/DateSelector'

//utils
import { MACHINES } from '../../utils/constant.utils'
import * as DATE_UTILS from '../../utils/date.utils'
import { showNotification } from '../../utils/toast.utils'

//service
import liveDataService from '../../service/liveData.service'

const liveDataTabs = {
  OVERVIEW: { name: 'Overview', value: 'overview' },
  PRODUCTION: { name: 'Production', value: 'hourlyTrend' },
  REJECTION: { name: 'Rejection', value: 'rejection' },
  IDLETIME: { name: 'Idletime', value: 'idletime' }
}

const customStyles = {
  container: (provided) => ({
    ...provided,
    width: '200px'
  }),
  control: (provided) => ({
    ...provided,
    height: '40px'
  })
}

const TabsContainer = styled('div')(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  padding: '8px 24px 0 0',
  borderBottom: `1px solid #eee`
}))

const Tab = styled('div')(({ theme, isActive }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  fontSize: '16px',
  minWidth: 'fit-content',
  padding: '8px 16px',
  color: isActive ? theme.palette.primary.main : '#ccccd7',
  cursor: 'pointer',
  borderBottom: isActive
    ? `1px solid ${theme.palette.primary.main}`
    : '1px solid transparent',
  '&:hover': {
    color: `${theme.palette.primary.main}`
  }
}))

const MachineStatus = () => {
  //default values
  let defaultTab = liveDataTabs.OVERVIEW.value
  let defaultMachine = {
    label: MACHINES[0].label,
    value: MACHINES[0].value
  }

  //hooks
  const navigate = useNavigate()
  const location = useLocation()

  //extract category from URL
  const params = new URLSearchParams(location.search)
  const tab = params.get('tab')
  const machine = params.get('machine')

  if (tab) {
    defaultTab = tab
  }

  if (machine) {
    defaultMachine = MACHINES.find((item) => item.value === machine)
  }

  //component state
  const [allData, setAllData] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [selectedMachine, setSelectedMachine] = useState(defaultMachine)
  const [activeSubTab, setActiveSubTab] = useState(defaultTab)
  const [availableShifts, setAvailableShifts] = useState([])
  const [selectedShift, setSelectedShift] = useState({
    label: 'S1',
    value: 'S1'
  })
  const [workingDate, setWorkingDate] = useState(DATE_UTILS.workingDate())
  const [calenderDate, setCalenderDate] = useState(DATE_UTILS.workingDate())

  const fetchMachineData = async () => {
    const params = {}

    if (selectedMachine.value !== 0) {
      params.machine = selectedMachine.value
    }

    if (workingDate) {
      params.date = workingDate
    }

    try {
      setIsLoading(true)
      const response = await liveDataService.getLiveData(params)
      // console.log(response);
      if (response.data.success) {
        const liveData = response.data.data
        setAllData(liveData)
        setAvailableShifts(
          Object.keys(liveData).map((shift) => ({
            label: shift,
            value: shift
          }))
        )
      } else {
        showNotification(response.data.message, 'error')
      }
    } catch (err) {
      console.log(err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchMachineData()
  }, [selectedMachine, workingDate])

  useEffect(() => {
    navigate(`?tab=${activeSubTab}&machine=${selectedMachine.value}`)
  }, [activeSubTab, selectedMachine])

  useEffect(() => {
    if (availableShifts.length > 0) {
      setSelectedShift(availableShifts[availableShifts.length - 1])
    }
  }, [availableShifts])

  const handleSubTabChange = (tab) => {
    setActiveSubTab(tab)
  }

  const dateChangeHandler = (e) => {
    setCalenderDate(e.target.value)
  }

  const submitHandler = (e) => {
    e.preventDefault()
    setWorkingDate(calenderDate)
  }

  const prevNextHandler = (actionType) => {
    setCalenderDate((prevDate) => DATE_UTILS.updateDate(actionType, prevDate))
    setWorkingDate((prevDate) => DATE_UTILS.updateDate(actionType, prevDate))
  }

  let liveDataMinuteWise = []
  if (
    allData[selectedShift.value] &&
    allData[selectedShift.value]['minuteWisData']
  ) {
    liveDataMinuteWise = allData[selectedShift.value]['minuteWisData']
  }

  let liveDataHourlyGrouped = []
  if (
    allData[selectedShift.value] &&
    allData[selectedShift.value]['hourlyGroupedData']
  ) {
    liveDataHourlyGrouped = allData[selectedShift.value]['hourlyGroupedData']
  }

  return (
    <StyledPageContainer>
      <Box sx={{ padding: '16px', borderBottom: '1px solid #eee' }}>
        <SingleMachineCard machine={selectedMachine.value} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '1px solid #eee'
        }}
      >
        <DateSelector
          date={workingDate}
          dateChangeHandler={dateChangeHandler}
          submitHandler={submitHandler}
          prevNextHandler={prevNextHandler}
          isLoading={isLoading}
        />
        <Box sx={{ display: 'flex', gap: '16px' }}>
          <ReactSelect
            styles={customStyles}
            placeholder="Select shift"
            options={availableShifts}
            value={selectedShift}
            onChange={(value) => setSelectedShift(value)}
          />
          <ReactSelect
            styles={customStyles}
            placeholder="Select Machine"
            options={MACHINES}
            value={selectedMachine}
            isSearchable
            noOptionsMessage={() => 'No machine Found'}
            onChange={(value) => setSelectedMachine(value)}
          />
        </Box>
      </Box>
      <TabsContainer>
        {React.Children.toArray(
          Object.values(liveDataTabs).map((tab, i) => (
            <Tab
              isActive={activeSubTab === tab.value}
              onClick={() => handleSubTabChange(tab.value)}
            >
              {tab.name}
            </Tab>
          ))
        )}
      </TabsContainer>
      <Box sx={{ padding: '16px' }}>
        {activeSubTab === liveDataTabs.OVERVIEW.value && (
          <Overview
            isLoading={isLoading}
            liveData={liveDataMinuteWise}
            groupedData={liveDataHourlyGrouped}
          />
        )}
        {activeSubTab === liveDataTabs.PRODUCTION.value && (
          <HourlyTrend
            isLoading={isLoading}
            groupedData={liveDataHourlyGrouped}
          />
        )}
        {activeSubTab === liveDataTabs.REJECTION.value && (
          <Rejection
            isLoading={isLoading}
            groupedData={liveDataHourlyGrouped}
          />
        )}
        {activeSubTab === liveDataTabs.IDLETIME.value && (
          <Idletime isLoading={isLoading} groupedData={liveDataHourlyGrouped} />
        )}
      </Box>
    </StyledPageContainer>
  )
}

export default MachineStatus
